/**
 * Fuzzy value matching utilities for matching GeoJSON property values
 * to dropdown options in the Property Mappings system.
 *
 * Uses Fuse.js for fuzzy string matching to handle typos, abbreviations,
 * and common variations (e.g., "Follow" → "Fallow", "corn" → "Corn").
 */

import Fuse from "fuse.js";
import { IdDropdownOption } from "./types";

// Define the IFuseOptions type locally since we can't import it with require
interface IFuseOptions {
	threshold?: number;
	useExtendedSearch?: boolean;
	includeScore?: boolean;
	ignoreLocation?: boolean;
	minMatchCharLength?: number;
	keys?: Array<string | { name: string; weight?: number }>;
}

/**
 * Default Fuse.js options for fuzzy matching.
 * Tuned for matching short strings like crop names and priorities.
 */
const DEFAULT_FUSE_OPTIONS: IFuseOptions = {
	// Lower threshold = more strict matching (0 = exact, 1 = match anything)
	threshold: 0.4,
	// Disable extended search patterns
	useExtendedSearch: false,
	// Include score in results for debugging
	includeScore: true,
	// Ignore location - match anywhere in the string
	ignoreLocation: true,
	// Minimum characters before fuzzy matching kicks in
	minMatchCharLength: 2,
};

// ============================================================================
// Fuse.js Instance Caching
// ============================================================================
// Creating a new Fuse instance rebuilds the search index, which is O(n).
// By caching instances, repeated searches on the same option set are O(1) lookup.

interface FuseCacheEntry<T> {
	fuse: Fuse<T>;
	timestamp: number;
}

const fuseCache = new Map<string, FuseCacheEntry<string>>();
const fuseCacheIdDropdown = new Map<string, FuseCacheEntry<IdDropdownOption>>();
const CACHE_MAX_SIZE = 50;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Generates a cache key for a set of options.
 * For small option sets, uses full content hash.
 * For large sets, uses a fingerprint to avoid expensive serialization.
 */
function generateCacheKey(options: string[], threshold: number): string {
	if (options.length <= 20) {
		return JSON.stringify(options) + ":" + threshold;
	}
	// For large arrays, use a fingerprint instead of full serialization
	return `${options.length}:${options[0]}:${options[options.length - 1]}:${threshold}`;
}

/**
 * Generates a cache key for IdDropdownOption arrays.
 */
function generateIdDropdownCacheKey(options: IdDropdownOption[], threshold: number): string {
	if (options.length <= 20) {
		return options.map((o) => `${o.id}:${o.name}`).join(",") + ":" + threshold;
	}
	return `${options.length}:${options[0]?.id}:${options[options.length - 1]?.id}:${threshold}`;
}

/**
 * Gets or creates a cached Fuse instance for string options.
 */
function getCachedFuse(options: string[], fuseOptions: IFuseOptions): Fuse<string> {
	const key = generateCacheKey(options, fuseOptions.threshold ?? 0.4);
	const cached = fuseCache.get(key);

	if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
		return cached.fuse;
	}

	const fuse = new Fuse(options, fuseOptions);

	// LRU eviction: remove oldest entry if at capacity
	if (fuseCache.size >= CACHE_MAX_SIZE) {
		const oldestKey = fuseCache.keys().next().value;
		if (oldestKey) fuseCache.delete(oldestKey);
	}

	fuseCache.set(key, { fuse, timestamp: Date.now() });
	return fuse;
}

/**
 * Gets or creates a cached Fuse instance for IdDropdownOption arrays.
 */
function getCachedFuseForIdDropdown(options: IdDropdownOption[], threshold: number): Fuse<IdDropdownOption> {
	const key = generateIdDropdownCacheKey(options, threshold);
	const cached = fuseCacheIdDropdown.get(key);

	if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
		return cached.fuse;
	}

	const fuseOptions = { ...DEFAULT_FUSE_OPTIONS, threshold, keys: ["name"] };
	const fuse = new Fuse(options, fuseOptions);

	// LRU eviction
	if (fuseCacheIdDropdown.size >= CACHE_MAX_SIZE) {
		const oldestKey = fuseCacheIdDropdown.keys().next().value;
		if (oldestKey) fuseCacheIdDropdown.delete(oldestKey);
	}

	fuseCacheIdDropdown.set(key, { fuse, timestamp: Date.now() });
	return fuse;
}

/**
 * Clears all Fuse instance caches.
 * Useful for testing to ensure test isolation.
 */
export function clearFuseCache(): void {
	fuseCache.clear();
	fuseCacheIdDropdown.clear();
}

// WeakMap cache for salesperson last-name options
const lastNameOptionsCache = new WeakMap<IdDropdownOption[], IdDropdownOption[]>();

/**
 * Normalizes a string for comparison by removing spaces, hyphens,
 * underscores and converting to lowercase.
 */
function normalize(str: string): string {
	return str.toLowerCase().replace(/[\s\-_]+/g, "");
}

/**
 * Fuzzy matches an input value against a list of valid options using Fuse.js.
 *
 * Matching priority (in order):
 * 1. Exact match (case-insensitive)
 * 2. Normalized match (spaces, hyphens, underscores removed)
 * 3. Fuse.js fuzzy match (handles typos, abbreviations, etc.)
 *
 * @param inputValue - The value to match (from GeoJSON property)
 * @param options - Valid dropdown options to match against
 * @param fuseOptions - Optional Fuse.js options override
 * @returns The best matching option, or null if no match found
 */
export function fuzzyMatchValue(
	inputValue: string | null | undefined,
	options: string[],
	fuseOptions?: IFuseOptions,
): string | null {
	if (inputValue === null || inputValue === undefined || inputValue === "") {
		return null;
	}

	if (options.length === 0) {
		return null;
	}

	// 1. Exact match (case-insensitive)
	const exactMatch = options.find((opt) => opt.toLowerCase() === inputValue.toLowerCase());
	if (exactMatch) return exactMatch;

	// 2. Normalized match (spaces, hyphens, underscores removed)
	const normalizedInput = normalize(inputValue);
	const normalizedMatch = options.find((opt) => normalize(opt) === normalizedInput);
	if (normalizedMatch) return normalizedMatch;

	// 3. Fuse.js fuzzy match (using cached Fuse instance)
	const effectiveOptions = fuseOptions ?? DEFAULT_FUSE_OPTIONS;
	const fuse = getCachedFuse(options, effectiveOptions);
	const results = fuse.search(inputValue);

	if (results.length > 0 && results[0].score !== undefined) {
		// Only return if score is good enough (lower is better in Fuse.js)
		if (results[0].score <= (fuseOptions?.threshold ?? DEFAULT_FUSE_OPTIONS.threshold!)) {
			return results[0].item;
		}
	}

	// No match - return null (user will select manually in ImportTable)
	return null;
}

// ============================================================================
// Sample ID detection
// ============================================================================

const SAMPLE_ID_KEY_ALIASES = ["id", "sample_id", "spl_id", "spl id", "SampleID", "PointID", "point_id", "SAMPLE_ID"];

/**
 * Given raw GeoJSON properties, find the value that represents sample_id.
 * Uses exact match on "sample_id" first, then fuzzy matching against aliases.
 */
export function fuzzyMatchSample(properties: Record<string, unknown> | null): string | null {
	if (!properties) return null;
	if ("sample_id" in properties && properties.sample_id) return String(properties.sample_id);
	for (const key of Object.keys(properties)) {
		const match = fuzzyMatchValue(key, SAMPLE_ID_KEY_ALIASES, { threshold: 0.3 });
		if (match) return properties[key] ? String(properties[key]) : null;
	}
	return null;
}

/**
 * Detect which property key represents sample_id. Returns the KEY name or null.
 */
export function detectSampleIdKey(properties: Record<string, unknown> | null): string | null {
	if (!properties) return null;
	if ("sample_id" in properties) return "sample_id";
	for (const key of Object.keys(properties)) {
		const match = fuzzyMatchValue(key, SAMPLE_ID_KEY_ALIASES, { threshold: 0.3 });
		if (match) return key;
	}
	return null;
}

/**
 * Crop-specific fuzzy matching with common agricultural synonyms.
 *
 * Handles common crop name variations:
 * - "soy", "beans", "soybean" → "Soybeans"
 * - "maize" → "Corn"
 * - "Follow" → "Fallow" (typos)
 * - Case variations (corn → Corn)
 * - Space/hyphen variations (soy beans → Soybeans)
 *
 * @param inputValue - The crop name from GeoJSON
 * @param cropOptions - Available crop options in the dropdown
 * @returns The matched crop option, or null if no match found
 */
export function fuzzyMatchCrop(inputValue: string | null | undefined, cropOptions: string[]): string | null {
	if (!inputValue) return null;

	// Actual crop synonyms - different words for the same crop
	// NOTE: Typos are NOT included here - Fuse.js handles those automatically
	const synonyms: Record<string, string[]> = {
		corn: ["maize"],
		soybeans: ["soy", "beans", "soybean"],
		alfalfa: ["lucerne"],
		sorghum: ["milo"],
	};

	const normalizedInput = normalize(inputValue);

	// Check synonyms first
	for (const [crop, aliases] of Object.entries(synonyms)) {
		// Check if input matches any alias
		if (aliases.some((alias) => normalize(alias) === normalizedInput)) {
			// Find the matching option in cropOptions
			const matchedOption = cropOptions.find((opt) => normalize(opt) === crop);
			if (matchedOption) return matchedOption;
		}
	}

	// Fall back to Fuse.js fuzzy matching with crop-specific options
	// Use slightly higher threshold for crops (more lenient matching)
	return fuzzyMatchValue(inputValue, cropOptions, {
		...DEFAULT_FUSE_OPTIONS,
		threshold: 0.35,
	});
}

/**
 * Priority-specific fuzzy matching.
 *
 * First tries direct fuzzy matching against the options (handles "Urgent" → "Urgent").
 * Then tries text-to-canonical mappings for legacy systems using numeric or text codes.
 *
 * Handles priority values that may be expressed as:
 * - Direct text: "Urgent", "Regular Turn", etc.
 * - Numbers: "1", "2", "3"
 * - Text codes: "high", "medium", "low"
 * - Variants: "top", "rush", "normal", "bottom"
 *
 * @param inputValue - The priority value from GeoJSON
 * @param priorityOptions - Available priority options (e.g., ["Urgent", "Regular Turn (default)", ...])
 * @returns The matched priority option, or null if no match found
 */
export function fuzzyMatchPriority(inputValue: string | null | undefined, priorityOptions: string[]): string | null {
	if (!inputValue) return null;

	const normalizedInput = normalize(inputValue);

	// Mappings from text/numeric codes to canonical priority names
	// Check these FIRST before fuzzy matching to avoid false positives like "rush" → "No Rush"
	const textToCanonical: Record<string, string[]> = {
		// High priority variants → try to match "Urgent" option
		"1": ["Urgent"],
		high: ["Urgent"],
		top: ["Urgent"],
		rush: ["Urgent"],
		first: ["Urgent"],
		priority: ["Urgent"],
		// Medium priority variants → try to match "Regular Turn" option
		"2": ["Regular Turn (default)", "Regular Turn"],
		medium: ["Regular Turn (default)", "Regular Turn"],
		mid: ["Regular Turn (default)", "Regular Turn"],
		normal: ["Regular Turn (default)", "Regular Turn"],
		second: ["Regular Turn (default)", "Regular Turn"],
		standard: ["Regular Turn (default)", "Regular Turn"],
		// Low priority variants → try to match "No Rush" option
		"3": ["No Rush (turn+1wk)", "No Rush"],
		low: ["No Rush (turn+1wk)", "No Rush"],
		bottom: ["No Rush (turn+1wk)", "No Rush"],
		last: ["No Rush (turn+1wk)", "No Rush"],
		third: ["No Rush (turn+1wk)", "No Rush"],
	};

	// Check if input matches any canonical mapping FIRST
	const canonicalCandidates = textToCanonical[normalizedInput];
	if (canonicalCandidates) {
		// Try each canonical candidate against the options
		for (const candidate of canonicalCandidates) {
			const match = priorityOptions.find((opt) => normalize(opt).startsWith(normalize(candidate)));
			if (match) return match;
		}
	}

	// Then try direct fuzzy matching (handles "Urgent" → "Urgent", "urgent" → "Urgent", etc.)
	const directMatch = fuzzyMatchValue(inputValue, priorityOptions);
	if (directMatch) return directMatch;

	// No match found
	return null;
}

/**
 * Test package fuzzy matching.
 *
 * Uses Fuse.js fuzzy matching for test package names.
 * Handles case variations, separator differences, and typos.
 *
 * @param inputValue - The test package name from GeoJSON
 * @param packageOptions - Available package options from deals
 * @returns The matched package option, or null if no match found
 */
export function fuzzyMatchTestPackage(inputValue: string | null | undefined, packageOptions: string[]): string | null {
	return fuzzyMatchValue(inputValue, packageOptions);
}

/**
 * Fuzzy matches an input value against ID dropdown options.
 * Matches against the 'name' field but returns the 'id'.
 *
 * This is the core function for ID-based dropdowns (Branch, Salesperson)
 * where the display value is different from the stored value.
 *
 * Optimized to avoid extracting names array and re-delegating to fuzzyMatchValue.
 * Uses cached Fuse instance for repeated searches on the same options.
 *
 * @param inputValue - The value to match (from GeoJSON property)
 * @param options - Array of {id, name} objects
 * @param threshold - Fuse.js threshold (default 0.4, same as fuzzyMatchValue)
 * @returns The matched option's ID, or null if no match found
 */
export function fuzzyMatchIdDropdown(
	inputValue: string | null | undefined,
	options: IdDropdownOption[],
	threshold?: number,
): string | null {
	if (!inputValue || options.length === 0) return null;

	// 1. Exact match (case-insensitive) - avoids Fuse entirely
	const exactMatch = options.find((opt) => opt.name.toLowerCase() === inputValue.toLowerCase());
	if (exactMatch) return exactMatch.id;

	// 2. Normalized match (spaces, hyphens, underscores removed)
	const normalizedInput = normalize(inputValue);
	const normalizedMatch = options.find((opt) => normalize(opt.name) === normalizedInput);
	if (normalizedMatch) return normalizedMatch.id;

	// 3. Fuzzy match with cached Fuse instance
	const effectiveThreshold = threshold ?? DEFAULT_FUSE_OPTIONS.threshold!;
	const fuse = getCachedFuseForIdDropdown(options, effectiveThreshold);
	const results = fuse.search(inputValue);

	if (results.length > 0 && results[0].score !== undefined) {
		if (results[0].score <= effectiveThreshold) {
			return results[0].item.id;
		}
	}

	return null;
}

/**
 * Branch-specific fuzzy matching.
 * Uses slightly more lenient threshold (0.35) for region/location names.
 *
 * @param inputValue - The branch name from GeoJSON
 * @param branchOptions - Array of {id, name} branch objects
 * @returns The matched branch ID, or null if no match found
 */
export function fuzzyMatchBranch(
	inputValue: string | null | undefined,
	branchOptions: IdDropdownOption[],
): string | null {
	return fuzzyMatchIdDropdown(inputValue, branchOptions, 0.35);
}

/**
 * Salesperson-specific fuzzy matching.
 * Handles full names, first names, and last names.
 *
 * Matching strategy:
 * 1. Try exact/fuzzy match on full name first
 * 2. If input is a single word, try matching against last names only
 *
 * Uses WeakMap caching for the last-name transformed options array
 * to avoid recreating it on every call.
 *
 * @param inputValue - The salesperson name from GeoJSON
 * @param salespersonOptions - Array of {id, name} salesperson objects
 * @returns The matched salesperson ID, or null if no match found
 */
export function fuzzyMatchSalesperson(
	inputValue: string | null | undefined,
	salespersonOptions: IdDropdownOption[],
): string | null {
	if (!inputValue) return null;

	// Try exact/fuzzy match on full name first
	const fullNameMatch = fuzzyMatchIdDropdown(inputValue, salespersonOptions, 0.35);
	if (fullNameMatch) return fullNameMatch;

	// If input looks like just a last name (single word), try matching last names
	const normalizedInput = inputValue.trim();
	if (!normalizedInput.includes(" ")) {
		// Get or create cached last-name options
		let lastNameOptions = lastNameOptionsCache.get(salespersonOptions);
		if (!lastNameOptions) {
			lastNameOptions = salespersonOptions.map((opt) => ({
				id: opt.id,
				name: opt.name.split(" ").pop() || opt.name, // Get last word as last name
			}));
			lastNameOptionsCache.set(salespersonOptions, lastNameOptions);
		}
		return fuzzyMatchIdDropdown(inputValue, lastNameOptions, 0.3);
	}

	return null;
}
